import { useEffect, useRef, useState, type ReactNode } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import BackToTop from '@/components/common/BackToTop';
import { useCabecalhoRetratil } from '@/hooks/useCabecalhoRetratil';
import { getStoredSidebarCollapsed, setStoredSidebarCollapsed } from '@/lib/sidebarPreference';

/** `gap-3` do shell — some junto com o cabeçalho quando ele se recolhe. */
const GAP_DO_SHELL = 12;

/**
 * Tempo e curva do recolhimento, por direção.
 *
 * **Assimétrico de propósito.** Sair e voltar não são o mesmo gesto: a saída
 * começa suave e acelera, como algo que ganha impulso ao ser empurrado; a
 * volta desacelera até parar, que é o que faz uma chegada parecer pousar em
 * vez de bater.
 *
 * **Desacelerar não é a mesma coisa que despencar.** A volta já usou
 * `cubic-bezier(.16,1,.3,1)`, que percorre 49% da distância nos primeiros 10%
 * do tempo e termina aos 70% — um estalo seguido de tempo morto, justamente o
 * oposto de suave. `cubic-bezier(.22,.61,.36,1)` distribui bem melhor
 * (27/50/67/87/96/100 em t = .1/.2/.3/.5/.7/.9) e chega ao fim junto com a
 * duração. Ao mexer nestas curvas, olhe a progressão em vez do nome dela.
 *
 * **A opacidade usa a mesma duração e a mesma curva do `margin-top`**, e isso
 * não é economia de configuração: assim `opacity` é sempre igual à fração do
 * cartão que ainda está fora do corte. O esmaecimento existe porque o
 * cabeçalho é cortado pela borda do `overflow-hidden` da coluna, e amarrá-lo
 * ao movimento é o que garante que nunca haja borda dura sobre pixel opaco.
 *
 * Já teve duração e atraso próprios (200ms na saída, 260ms com 60ms de atraso
 * na volta) e o resultado era desencontrado nas duas pontas — na volta o pior:
 * aos 80ms o cartão estava 68% opaco com 41% ainda cortado, e a guilhotina
 * aparecia durante a entrada inteira.
 *
 * **A duração aqui é orçamento de layout, não só ritmo.** `margin-top` não é
 * animável pelo compositor: cada frame da transição refaz o layout da coluna,
 * redimensiona o `<main>` — que é o container de rolagem — e repinta o painel
 * inteiro. O custo da animação é, literalmente, quantos frames ela dura. Daí
 * 180/220ms no lugar de 280/320: 11 e 13 frames a 60fps em vez de 17 e 19, um
 * terço a menos de passadas de layout no pior momento possível, que é durante
 * o gesto de rolagem.
 *
 * **As curvas continuam as mesmas, e isso é a escolha.** Encurtar não mexe na
 * forma da progressão, só no tempo real de cada fase. Na volta, a desaceleração
 * final (dos 90% ao fim) cai de 146ms para 100ms — ainda bem acima dos ~50ms em
 * que um ease-out deixa de pousar e passa a parar seco. Na saída, a cabeça
 * lenta (até 30% do percurso) cai de 133ms para 86ms, e segue legível como
 * impulso. Encurtar mais que isso não economiza layout que valha o que a
 * assimetria perde.
 */
const TRANSICAO = {
  recolher: { duracao: '180ms', curva: 'cubic-bezier(.4, 0, 1, 1)' },
  voltar: { duracao: '220ms', curva: 'cubic-bezier(.22, .61, .36, 1)' },
} as const;

interface Props {
  children: ReactNode;
}

export default function BackofficeLayout({ children }: Readonly<Props>) {
  const [collapsed, setCollapsed] = useState(getStoredSidebarCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  /** Quem rola é o `<main>`, não a janela — ver `BackToTop`. */
  const mainRef = useRef<HTMLElement>(null);
  const { cabecalhoRef, recolhido, deslocamento, revelar } = useCabecalhoRetratil<HTMLDivElement>(
    mainRef,
    GAP_DO_SHELL,
  );

  useEffect(() => {
    setStoredSidebarCollapsed(collapsed);
  }, [collapsed]);

  return (
    <div className="flex h-screen overflow-hidden bg-shell">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />
      {/* `relative`: é a âncora do `BackToTop`, que precisa ficar parado sobre o
          painel. Absoluto dentro do `<main>` ele rolaria junto com o conteúdo. */}
      <div className="relative flex flex-col flex-1 gap-3 overflow-hidden p-3">
        {/*
          O cabeçalho se recolhe para cima ao rolar o painel, e o `<main>`
          cresce no lugar. A margem negativa é o que devolve o espaço — só
          transladar tiraria da vista sem liberar nada.

          `onFocusCapture` traz de volta quando algo dentro dele recebe foco:
          recolhido, o cabeçalho fica fora da tela, e quem chega no menu do
          usuário por Tab não pode acabar com o foco num lugar invisível.
        */}
        <div
          ref={cabecalhoRef}
          onFocusCapture={revelar}
          style={{
            marginTop: deslocamento,
            opacity: recolhido ? 0 : 1,
            transitionDuration: TRANSICAO[recolhido ? 'recolher' : 'voltar'].duracao,
            transitionTimingFunction: TRANSICAO[recolhido ? 'recolher' : 'voltar'].curva,
          }}
          className="shrink-0 transition-[margin-top,opacity] motion-reduce:transition-none"
        >
          <Header mobileOpen={mobileOpen} onOpenMobileMenu={() => setMobileOpen(true)} />
        </div>
        <main ref={mainRef} className="flex-1 overflow-y-auto rounded-xl bg-surface-sunken p-6 shadow-lg">
          {children}
        </main>
        <BackToTop scrollRef={mainRef} />
      </div>
    </div>
  );
}
